import {
  ChangeDetectionStrategy,
  Component,
  ElementRef, Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit, PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {CombinedCryptoData} from "../../../../../core/models/market.model";
import {fromEvent, Observable} from "rxjs";
import {SubSink} from "subsink";
import * as d3 from 'd3';
import {HierarchyNode} from "d3";
import Big from 'big.js';
import {DecimalPipe, isPlatformBrowser} from "@angular/common";
import {SummariseNumberPipe} from "../../../../../shared/pipes/summarise-number.pipe";
import {HttpParams} from "@angular/common/http";
import {MarketDataService} from "../../services/market-data.service";
import {basename} from "@angular/compiler-cli";
import {debounceTime} from "rxjs/operators";
import {Router} from "@angular/router";

@Component({
  selector: 'bit-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss'],
  providers: [MarketDataService, HttpParams, SummariseNumberPipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeatmapComponent implements OnInit, OnDestroy {
  @ViewChild('treemap', {static: true}) treemap!: ElementRef;
  @ViewChild('treemap_container', {static: true}) treemap_container!: ElementRef;
  @ViewChild('tooltip', {static: true}) tooltip!: ElementRef;

  @Input('newData$') set newData$(d: Observable<CombinedCryptoData[] | undefined>) {
    this.sub.sink = d?.subscribe((el: CombinedCryptoData[] | undefined) => {
      if (this.isFocused) {
        let a = el?.filter((market) => {
          return market.category === this.focusedDataMarket![0].category
        })
        this.treemapConfig(a, this.mainWidth, this.mainHeight)
      } else
        this.treemapConfig(el, this.mainWidth, this.mainHeight)
    })
  };
  @Input() quoteCurrencyId!: string;
  @Input() treemapData!: Observable<CombinedCryptoData[] | undefined>;
  @Input('width') set width(n: number) {
    this.mainWidth = n;
  }
  @Input() set height(n: number) {
    this.mainHeight = n;
  }
  @Input() isFullScreen = true;
  private mainWidth!: number;
  private mainHeight!: number;
  marketType!: string;
  private markets: CombinedCryptoData[] | undefined;
  fullScreen = false;
  private isFocused = false;

  private sub = new SubSink();
  private solidData!: { children: { name: string; children: any[] }[]; name: string };
  private focusedDataMarket: CombinedCryptoData[] | undefined

  private lessWidth: number = 30;
  private lessHeight: number = 20;
  private fontRation: number = 10;
  private tooltipHeight: number = 238;
  private tooltipWidth: number = 210;

  private svg!: d3.Selection<any, unknown, null, undefined>;

  constructor(
    private zone: NgZone,
    private priceNumber: SummariseNumberPipe,
    private decimalPipe: DecimalPipe,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.marketType = this.quoteCurrencyId;
      this.sub.sink = this.treemapData.subscribe((markets) => {
        this.markets = markets;
        setTimeout(() => {
          this.treemapConfig();
        }, 100);
      });

      if (isPlatformBrowser(this.platformId)) {
        this.sub.sink = fromEvent(window, 'resize')
          .pipe(debounceTime(16))
          .subscribe(() => {
            this.mainWidth = this.treemap_container.nativeElement.offsetWidth;
            this.mainHeight = this.treemap_container.nativeElement.offsetHeight;
            this.treemapConfig(this.focusedDataMarket ?? this.markets, this.mainWidth, this.mainHeight);
          });
      }
    });
  }

  treemapConfig(data = this.markets, width = this.mainWidth, height = this.mainHeight) {
    setTimeout(() => {
      this.createNodes(data, width, height);
    });
  }

  createNodes(data = this.markets, width = this.mainWidth, height = this.mainHeight) {
    this.zone.runOutsideAngular(() => {
      if (this.svg !== undefined) {
        for (const child of this.treemap.nativeElement.children) {
          child.remove();
        }
      }
      this.convertToSolidData(data);
      let widthAndHeight: [number, number];
      if (this.fullScreen) {
        widthAndHeight = [window.innerWidth, window.innerHeight];
      } else widthAndHeight = [width, height];
      this.svg = d3
        .select(this.treemap.nativeElement)
        .append('svg')
        .attr('width', widthAndHeight[0])
        .attr('height', widthAndHeight[1])
        .attr('xmlns', 'http://www.w3.org/2000/svg')
        .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
        .attr('xmlns', 'http://www.w3.org/1999/xhtml')
        .append('g')
        .attr('transform', `translate(0, 0)`);

      const root = d3
        .hierarchy(this.solidData)
        .sum((market: any) => market.volume)
        .sort(
          (node1: any, node2: any): number => node2['data']['volume'] - node1['data']['volume']
        );

      d3.treemap().padding(1).paddingTop(20).size(widthAndHeight)(root as HierarchyNode<unknown>);

      this.svg
        .selectAll('rect')
        .data(root.leaves())
        .join('rect')
        .attr('x', (d: any) => {
          return d.x0;
        })
        .attr('y', (d: any) => {
          return d.y0;
        })
        .attr('width', (d: any) => d.x1 - d.x0)
        .attr('height', (d: any) => d.y1 - d.y0)
        .attr('fill', (coin: any) => {
          let percent = coin.data.price24hChange;
          if (+percent === 0) return 'gray';
          if (+percent >= 5) return '#315c32';
          if (+percent >= 3) return '#5c8456';
          if (+percent > 0) return '#8cbf84';

          if (+percent <= -5) return '#9d2f29';
          if (+percent <= -3) return '#ba4a45';
          if (+percent < 0) return '#de7875';
          return '';
        });

      let selection = this.svg
        .selectAll('foreignObject')
        .join('foreignObject')
        .data(root.leaves())
        .enter()
        .append('foreignObject')
        .attr('width', (d: any) => d.x1 - d.x0)
        .attr('height', (d: any) => d.y1 - d.y0)
        .attr('x', (d: any) => {
          return d.x0;
        })
        .attr('y', (d: any) => {
          return d.y0;
        });
      let content = selection
        .append('xhtml:div')
        .attr('class', 'flex flex-col justify-center items-center h-full cursor-pointer')
        .on('mousemove', (el, data) => {
          this.drawTooltip(el, data);
        })
        .on('mouseleave', () => {
          this.tooltip.nativeElement.setAttribute('style', 'display: none');
        })
        .on('click', (el, data: any) => {
          window.open(`https://app.bit.ir/market/${data.data.id}/${data.data.quoteCurrencyId === 'IRT' ? 'IRR' : data.data.quoteCurrencyId}`, '_blank');
        })
      ;
      content
        .append('p')
        .text((text: any) => {
          if (text.x1 - text.x0 > this.lessWidth && text.y1 - text.y0 > this.lessHeight) {
            return text.data.baseCurrencyId;
          } else {
            return '';
          }
        })
        .style('font-size', (font: any) => {
          let nodeWidth = font.x1 - font.x0;
          let nodeHeight = font.y1 - font.y0;
          if (nodeWidth < nodeHeight) return nodeWidth / 80 + 'em';
          else return nodeHeight / 66 + 'em';
        })
        .style('font-weight', 600)
        .style('text-shadow', '0.025em 0.025em 0 rgb(50 49 54 / 28%)')
        // .style('font-family', 'IRANSans')
        .attr('class', 'text-white text-ellipsis m-0');
      content
        .append('p')
        .style('margin', 0)
        .style('text-shadow', '0.025em 0.025em 0 rgb(50 49 54 / 28%)')
        // .style('font-family', 'IRANSans')
        .style('margin-bottom', 0)
        .text((price: any) => {
          let x = price.x1 - price.x0;
          if (x > this.lessWidth && price.y1 - price.y0 > this.lessHeight) {
            return (
              this.decimalPipe.transform(Big(price.data.lastPrice)
                .toFixed(price.data.quoteCurrencyPrecision, Big.roundDown), `1.0-${price.data.quoteCurrencyPrecision}`)! + ' ' +
              this.marketType
            );
          }
          return '';
        })
        // .style('font-family', 'IRANSans')
        .attr('class', 'text-white text-center')
        .style('font-size', (font: any) => {
          let width1 = font.x1 - font.x0;
          let height1 = font.y1 - font.y0;
          if (width1 < height1) return width1 / this.fontRation + 'px';
          else return height1 / this.fontRation + 'px';
        })
        .attr('fill', 'white');
      content
        .append('p')
        .style('margin', 0)
        .text((percent: any) => {
          if (
            percent.x1 - percent.x0 > this.lessWidth &&
            percent.y1 - percent.y0 > this.lessHeight
          ) {
            return percent.data.price24hChange + '%';
          } else {
            return '';
          }
        })
        .style('font-size', (font: any) => {
          let width1 = font.x1 - font.x0;
          let height1 = font.y1 - font.y0;
          if (width1 < height1) return width1 / this.fontRation + 'px';
          else return height1 / this.fontRation + 'px';
        })
        // .style('font-family', 'var(--font-en)')
        .style('text-shadow', '0.025em 0.025em 0 rgb(50 49 54 / 28%)')
        .style('color', 'white')
        .style('direction', 'ltr');

      this.svg
        .selectAll('titles')
        .data(root.descendants().filter((d) => d.depth == 1))
        .enter()
        .append('foreignObject')
        .attr('x', (d: any) => d.x0)
        .attr('y', (d: any) => d.y0)
        .attr('width', (d: any) => d.x1 - d.x0)
        .attr('height', () => 20)
        .append('xhtml:p')
        .text((d) => {
          return d.data.name;
        })
        .style('direction', 'ltr')
        // .style('font-family', 'IRANSans')
        // .style('text-overflow', 'ellipsis')
        .style('border-radius', '0.25rem')
        .attr('class', 'cursor-pointer text-en hover:text-[#000] hover:bg-yellow-700 transition-all pl-1 pb-1 overflow-hidden text-ellipsis')
        .on('click', (event, d: any) => {
          this.isFocused = !this.isFocused;
          if (this.isFocused) {
            let newCategoryData = this.solidData.children.filter(
              (el) => el.name === d.data.name
            );
            this.focusedDataMarket = newCategoryData[0].children
            this.treemapConfig(newCategoryData[0].children, this.mainWidth, this.mainHeight);
          } else {
            this.focusedDataMarket = []
            this.treemapConfig(this.markets, this.mainWidth, this.mainHeight);
          }
        })
    });
  }

  drawTooltip(mouse: any, data: any) {
    this.zone.runOutsideAngular(() => {
      let my_tooltip = this.tooltip.nativeElement;
      let mouseX: number;
      let mouseY: number;

      mouseX = mouse.pageX + 10;
      mouseY = mouse.pageY + 10;

      if (window.innerWidth - this.tooltipWidth < mouseX) {
        mouseX = mouseX - this.tooltipWidth - 10; // for mouse Location
      }
      if (window.innerHeight - this.tooltipHeight < mouseY) {
        mouseY = mouseY - this.tooltipHeight; // for mouse y location
      }

      my_tooltip.setAttribute(
        'style',
        `display: block; top: ${mouseY + 'px'}; left: ${mouseX + 'px'}`
      );
      let percent = document.getElementById('percentage');

      document.getElementById('symbol')!.innerText = data.data.baseCurrencyId;
      percent!.innerText = data.data.price24hChange + '%';

      if (+data.data.price24hChange > 0) {
        percent!.classList.remove('bg-red-500');
        percent!.classList.remove('bg-zinc-700');
        percent!.classList.add('bg-emerald-500');
      } else if (+data.data.price24hChange < 0) {
        percent!.classList.remove('bg-emerald-500');
        percent!.classList.remove('bg-zinc-700');
        percent!.classList.add('bg-red-500');
      } else if (+data.data.price24hChange === 0) {
        percent!.classList.remove('bg-red-500');
        percent!.classList.remove('bg-emerald-500');
        percent!.classList.add('bg-zinc-700');
      }


      document.getElementById('price')!.innerText =
        Number(data.data.lastPrice).toLocaleString('en-GB') + ' ' + this.marketType;
      document.getElementById('volume')!.innerText = this.priceNumber.transform(data.data.volume);
    });
  }

  convertToSolidData(data: any, title: string = 'crypto') {
    const category = new Map<string, any[]>();

    data.forEach((d: any) => {
      if (category.has(d.category)) {
        const a = category.get(d.category);
        if (a) {
          a.push(d);
        }
      } else {
        category.set(d.category, [d]);
      }
    });

    const b: { name: string; children: any[] }[] = [];
    Array.from(category.keys()).forEach((k) => {
      const found = category.get(k);
      if (found) {
        b.push({name: k, children: found});
      }
    });
    this.solidData = {name: title, children: b};
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

}
